import { ReactNode } from 'react';
import { Contact } from '../../components/address-book';

export type AddressSelectionProviderProps = {
  children: ReactNode;
};

export type AddressSelectionStatus = {
  // true if the user is adding the first address,
  // false if the user already has addresses
  // (recipient or sender stored in the backend)
  isInitialAddress: boolean;
  // true if the user is adding a recipient address,
  // false if the user is adding a sender address (default)
  isRecipientAddress: boolean;
  // true if the maximum number of addresses has been reached
  isMaxCountReached: boolean;
};

export type AddressSelectionContextState = {
  // the sender address that is stored in the backend
  sender: Contact | null;
  // the recipient addresses that are stored in the backend
  contacts: Contact[];
  // the current number of addresses that are added
  count: number;
  // the maximum number of addresses that can be added
  maxCount: number;
  // the status of the address selection for decision making
  status: AddressSelectionStatus;
};

export enum AddressSelectionType {
  SET_SENDER = 'ADD_SENDER',
  SET_CONTACTS = 'ADD_CONTACTS',
  SET_COUNT = 'SET_COUNT',
  INCREASE_COUNT = 'INCREASE_COUNT',
  DECREASE_COUNT = 'DECREASE_COUNT',
  SET_MAX_COUNT = 'SET_MAX_COUNT',
  RESET_COUNT = 'RESET_COUNT',
  RESET_ADDRESSES = 'RESET_ADDRESSES',
  SET_IS_MAX_COUNT_REACHED = 'SET_IS_MAX_COUNT_REACHED',
  SET_STATUS = 'SET_STATUS',
}

export type SetAddressSelectionSenderAction = {
  type: AddressSelectionType.SET_SENDER;
  payload: Contact;
};

export type SetAddressSelectionContactsAction = {
  type: AddressSelectionType.SET_CONTACTS;
  payload: Contact[];
};

export type SetAddressSelectionCountAction = {
  type: AddressSelectionType.SET_COUNT;
  payload: number;
};

export type ResetAddressSelectionCountAction = {
  type: AddressSelectionType.RESET_COUNT;
};

export type ResetAddressesAction = {
  type: AddressSelectionType.RESET_ADDRESSES;
  payload: AddressSelectionContextState;
};

export type IncrementAddressSelectionCountAction = {
  type: AddressSelectionType.INCREASE_COUNT;
};

export type DecrementAddressSelectionCountAction = {
  type: AddressSelectionType.DECREASE_COUNT;
};

export type SetAddressSelectionCountMaxAction = {
  type: AddressSelectionType.SET_MAX_COUNT;
  payload: number;
};

export type SetIsMaxCountReachedAction = {
  type: AddressSelectionType.SET_IS_MAX_COUNT_REACHED;
  payload: boolean;
};

export type SetAddressSelectionStatusAction = {
  type: AddressSelectionType.SET_STATUS;
  payload: AddressSelectionStatus;
};

export type AddressSelectionAction =
  | SetAddressSelectionSenderAction
  | SetAddressSelectionContactsAction
  | SetAddressSelectionCountAction
  | ResetAddressSelectionCountAction
  | ResetAddressesAction
  | IncrementAddressSelectionCountAction
  | DecrementAddressSelectionCountAction
  | SetAddressSelectionCountMaxAction
  | SetIsMaxCountReachedAction
  | SetAddressSelectionStatusAction;

export type Dispatch = (action: AddressSelectionAction) => void;

export type CreateContextProps =
  | {
      addressSelectionState: AddressSelectionContextState;
      addressSelectionDispatch: Dispatch;
    }
  | undefined;
